/**
 * Cart API Module
 * Centralized Shopify Cart API wrapper with event dispatching
 * 
 * Events dispatched:
 * - cart:item-added: { item, cart }
 * - cart:item-removed: { item, cart }
 * - cart:quantity-updated: { item, oldQuantity, newQuantity, cart }
 * - cart:updated: { cart }
 * - cart:changing: { action, ... } — cart mutation started (pending count > 0)
 * - cart:idle: {} — no pending cart mutations
 * - cart:error: { message, status }
 */



(function() {
  'use strict';

  // Request queue to prevent race conditions when rapidly modifying cart
  let requestQueue = Promise.resolve();
  let pendingOperations = 0;

  function dispatch(eventName, detail) {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  function beginChange(detail) {
    pendingOperations += 1;
    if (pendingOperations === 1) {
      dispatch('cart:changing', detail || {});
    }
  }

  function endChange() {
    pendingOperations = Math.max(0, pendingOperations - 1);
    if (pendingOperations === 0) {
      dispatch('cart:idle', {});
    }
  }

  function queueRequest(fn, detail) {
    return (requestQueue = requestQueue.then(async () => {
      beginChange(detail || { action: 'queued' });
      try {
        return await fn();
      } finally {
        endChange();
      }
    }));
  }

  const CartAPI = {
    /**
     * Get current cart state
     * @returns {Promise<Object>} Cart data
     */
    async getCart() {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        
        return await response.json();
      } catch (error) {
        this.dispatchError('Failed to load cart');
        throw error;
      }
    },

    /**
     * Add item(s) to cart
     * @param {number|string} variantId - Product variant ID
     * @param {number} quantity - Quantity to add (default: 1)
     * @param {Object} properties - Optional line item properties
     * @returns {Promise<Object>} Added item data
     */
    
    async addItem(variantId, quantity = 1, properties = {}) {
      beginChange({ action: 'addItem', variantId });
      try {
        const formData = {
          id: variantId,
          quantity: quantity
        };
        
        if (Object.keys(properties).length > 0) {
          formData.properties = properties;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMessage = data.description || data.message || 'Failed to add item to cart';
          this.dispatchError(errorMessage, response.status);
          throw new Error(errorMessage);
        }

        // Fetch updated cart state
        const cart = await this.getCart();

        // Dispatch item added event
        this.dispatch('cart:item-added', {
          item: {
            id: data.id,
            variant_id: data.variant_id,
            product_id: data.product_id,
            title: data.product_title,
            variant_title: data.variant_title,
            quantity: data.quantity,
            price: data.price,
            line_price: data.line_price,
            image: data.image,
            url: data.url,
            handle: data.handle
          },
          cart: cart
        });

        // Also dispatch general update event
        this.dispatch('cart:updated', { cart });

        return data;
      } catch (error) {
        console.error('Cart API - Add item error:', error);
        throw error;
      } finally {
        endChange();
      }
    },

    /**
     * Add multiple items to cart
     * @param {Array<{id: number, quantity: number, properties?: Object}>} items
     * @returns {Promise<Object>} Response data
     */
    async addItems(items) {
      beginChange({ action: 'addItems', count: items?.length || 0 });
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMessage = data.description || data.message || 'Failed to add items to cart';
          this.dispatchError(errorMessage, response.status);
          throw new Error(errorMessage);
        }

        // Fetch updated cart state
        const cart = await this.getCart();

        // Dispatch event for each item added
        if (data.items && Array.isArray(data.items)) {
          data.items.forEach(item => {
            this.dispatch('cart:item-added', {
              item: {
                id: item.id,
                variant_id: item.variant_id,
                product_id: item.product_id,
                title: item.product_title,
                variant_title: item.variant_title,
                quantity: item.quantity,
                price: item.price,
                line_price: item.line_price,
                image: item.image,
                url: item.url,
                handle: item.handle
              },
              cart: cart
            });
          });
        }

        // Dispatch general update event
        this.dispatch('cart:updated', { cart });

        return data;
      } catch (error) {
        console.error('Cart API - Add items error:', error);
        throw error;
      } finally {
        endChange();
      }
    },

    /**
     * Update item quantity by line item key
     * @param {string} key - Line item key
     * @param {number} quantity - New quantity (0 to remove)
     * @returns {Promise<Object>} Updated cart data
     */
    async updateItem(key, quantity) {
      return queueRequest(async () => {
        try {
          // Get current cart to find old quantity and validate item exists
          const oldCart = await this.getCart();
          const oldItem = oldCart.items.find(item => item.key === key);
          
          // If item doesn't exist in cart anymore (already removed), just return current cart
          if (!oldItem) {
            console.warn('Cart API - Item no longer in cart:', key);
            this.dispatch('cart:updated', { cart: oldCart });
            return oldCart;
          }
          
          const oldQuantity = oldItem.quantity;

          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: key,
              quantity: quantity
            })
          });

          const cart = await response.json();

          if (!response.ok) {
            // Handle "no valid id" error gracefully - item was already removed
            if (cart.description && cart.description.includes('no valid id')) {
              console.warn('Cart API - Item already removed:', key);
              const currentCart = await this.getCart();
              this.dispatch('cart:updated', { cart: currentCart });
              return currentCart;
            }
            const errorMessage = cart.description || cart.message || 'Failed to update cart';
            this.dispatchError(errorMessage, response.status);
            throw new Error(errorMessage);
          }

          // Find the updated item (or null if removed)
          const updatedItem = cart.items.find(item => item.key === key);

          if (quantity === 0 && oldItem) {
            // Item was removed
            this.dispatch('cart:item-removed', {
              item: {
                id: oldItem.id,
                variant_id: oldItem.variant_id,
                product_id: oldItem.product_id,
                title: oldItem.product_title,
                variant_title: oldItem.variant_title,
                quantity: oldQuantity,
                price: oldItem.price,
                image: oldItem.image
              },
              cart: cart
            });
          } else if (updatedItem) {
            // Quantity was updated
            this.dispatch('cart:quantity-updated', {
              item: {
                id: updatedItem.id,
                variant_id: updatedItem.variant_id,
                product_id: updatedItem.product_id,
                title: updatedItem.product_title,
                variant_title: updatedItem.variant_title,
                price: updatedItem.price,
                image: updatedItem.image,
                key: updatedItem.key
              },
              oldQuantity: oldQuantity,
              newQuantity: quantity,
              cart: cart
            });
          }

          // Dispatch general update event
          this.dispatch('cart:updated', { cart });

          return cart;
        } catch (error) {
          console.error('Cart API - Update item error:', error);
          throw error;
        }
      }, { action: 'updateItem', key, quantity });
    },

    /**
     * Remove item from cart
     * @param {string} key - Line item key
     * @returns {Promise<Object>} Updated cart data
     */
    async removeItem(key) {
      return this.updateItem(key, 0);
    },

    /**
     * Update multiple items
     * @param {Object} updates - Object with line item keys as keys and quantities as values
     * @returns {Promise<Object>} Updated cart data
     */
    async updateItems(updates) {
      beginChange({ action: 'updateItems' });
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });

        const cart = await response.json();

        if (!response.ok) {
          const errorMessage = cart.description || cart.message || 'Failed to update cart';
          this.dispatchError(errorMessage, response.status);
          throw new Error(errorMessage);
        }

        // Dispatch general update event
        this.dispatch('cart:updated', { cart });

        return cart;
      } catch (error) {
        console.error('Cart API - Update items error:', error);
        throw error;
      } finally {
        endChange();
      }
    },

    /**
     * Clear the entire cart
     * @returns {Promise<Object>} Empty cart data
     */
    async clearCart() {
      beginChange({ action: 'clearCart' });
      try {
        const response = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const cart = await response.json();

        if (!response.ok) {
          const errorMessage = cart.description || cart.message || 'Failed to clear cart';
          this.dispatchError(errorMessage, response.status);
          throw new Error(errorMessage);
        }

        // Dispatch update event
        this.dispatch('cart:updated', { cart });

        return cart;
      } catch (error) {
        console.error('Cart API - Clear cart error:', error);
        throw error;
      } finally {
        endChange();
      }
    },

    beginChange,
    endChange,

    get isPending() {
      return pendingOperations > 0;
    },

    /**
     * Dispatch a custom event
     * @param {string} eventName - Event name
     * @param {Object} detail - Event detail data
     */
    dispatch(eventName, detail) {
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
    },

    /**
     * Dispatch an error event
     * @param {string} message - Error message
     * @param {number} status - HTTP status code
     */
    dispatchError(message, status = 0) {
      this.dispatch('cart:error', { message, status });
    },

    /**
     * Format money according to Shopify settings
     * @param {number} cents - Price in cents
     * @returns {string} Formatted price
     */
    formatMoney(cents) {
      if (window.formatMoney) {
        return window.formatMoney(cents, window.cartPageConfig?.moneyFormat);
      }
      if (window.formatMoneyFallback) {
        return window.formatMoneyFallback(cents);
      }
      return (Number(cents || 0) / 100).toFixed(2);
    }
  };

  // Expose globally
  window.CartAPI = CartAPI;
})();

